import React, { useMemo, useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { LoadingSpinner } from '../../ToolExecution/LoadingSpinner';
import { formatNumber } from './constants';

import { useTranslation } from 'react-i18next';

export interface HierarchyRow {
  /** Unique within its sibling group. */
  id: string;
  label: string;
  /** Optional small label rendered next to the main label (e.g. identity type). */
  hint?: string;
  totalTokens: number;
  messageCount: number;
  conversationCount: number;
  children?: HierarchyRow[];
}

const LEVEL_INDENT_PX = 20;

interface RowProps {
  row: HierarchyRow;
  depth: number;
  expandedKeys: Set<string>;
  toggle: (key: string) => void;
  parentPath: string;
  zebraIndex: { i: number };
  primaryHeaderByDepth: string[];
}

function RowEntry({
  row,
  depth,
  expandedKeys,
  toggle,
  parentPath,
  zebraIndex,
  primaryHeaderByDepth,
}: RowProps): React.ReactElement {
  const key = `${parentPath}/${row.id}`;
  const hasChildren = !!row.children && row.children.length > 0;
  const isOpen = expandedKeys.has(key);
  const indent = depth * LEVEL_INDENT_PX;
  const myZebra = zebraIndex.i++;
  const baseBg =
    myZebra % 2 === 0
      ? 'bg-white dark:bg-gray-900'
      : 'bg-gray-50/30 dark:bg-gray-800/30';
  const rowBg = hasChildren
    ? `${baseBg} cursor-pointer transition-colors hover:bg-orange-50/60 dark:hover:bg-orange-900/10`
    : baseBg;

  return (
    <>
      <tr
        className={rowBg}
        onClick={hasChildren ? () => toggle(key) : undefined}
        role={hasChildren ? 'button' : undefined}
        aria-expanded={hasChildren ? isOpen : undefined}
        tabIndex={hasChildren ? 0 : undefined}
        onKeyDown={
          hasChildren
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggle(key);
                }
                if (e.key === 'ArrowRight' && !isOpen) {
                  e.preventDefault();
                  toggle(key);
                }
                if (e.key === 'ArrowLeft' && isOpen) {
                  e.preventDefault();
                  toggle(key);
                }
              }
            : undefined
        }
      >
        <td className="px-5 py-3 text-sm leading-relaxed font-medium text-gray-900 dark:text-gray-100">
          <div
            className="flex items-center gap-1.5"
            style={{ paddingLeft: indent }}
          >
            {hasChildren ? (
              isOpen ? (
                <ChevronDown
                  className="h-3.5 w-3.5 flex-shrink-0 text-gray-500 dark:text-gray-400"
                  strokeWidth={2}
                />
              ) : (
                <ChevronRight
                  className="h-3.5 w-3.5 flex-shrink-0 text-gray-500 dark:text-gray-400"
                  strokeWidth={2}
                />
              )
            ) : (
              <span
                className="inline-block h-3.5 w-3.5 flex-shrink-0"
                aria-hidden
              />
            )}
            <span className="truncate" title={row.label}>
              {row.label}
            </span>
            {row.hint && (
              <span className="text-xs leading-none font-normal text-gray-500 dark:text-gray-500">
                · {row.hint}
              </span>
            )}
            {depth < primaryHeaderByDepth.length - 1 && (
              <span className="ml-2 hidden text-xs leading-none font-normal tracking-wide text-gray-400 uppercase sm:inline dark:text-gray-500">
                {primaryHeaderByDepth[depth]}
              </span>
            )}
          </div>
        </td>
        <td className="px-5 py-3 text-right text-sm leading-relaxed text-gray-600 tabular-nums dark:text-gray-400">
          {formatNumber(row.messageCount)}
        </td>
        <td className="px-5 py-3 text-right text-sm leading-relaxed text-gray-600 tabular-nums dark:text-gray-400">
          {formatNumber(row.conversationCount)}
        </td>
        <td className="px-5 py-3 text-right text-sm leading-relaxed text-gray-700 tabular-nums dark:text-gray-300">
          {formatNumber(row.totalTokens)}
        </td>
      </tr>
      {isOpen &&
        row.children?.map((child) => (
          <RowEntry
            key={`${key}/${child.id}`}
            row={child}
            depth={depth + 1}
            expandedKeys={expandedKeys}
            toggle={toggle}
            parentPath={key}
            zebraIndex={zebraIndex}
            primaryHeaderByDepth={primaryHeaderByDepth}
          />
        ))}
    </>
  );
}

const BREAKDOWN_PAGE_SIZE = 25;

/**
 * Nested breakdown table. Top-level rows show summed totals across the
 * subtree; click ▸ to expand to the next level. Levels are labelled by
 * `primaryHeaderByDepth` (e.g. `['Tenant', 'User', 'Engagement']`).
 */
export function HierarchyBreakdownTable({
  rows,
  primaryHeaderByDepth,
  isLoading,
  emptyLabel = 'No usage for this period.',
}: {
  rows: HierarchyRow[];
  primaryHeaderByDepth: string[];
  isLoading: boolean;
  emptyLabel?: string;
}) {
  const { t } = useTranslation('pleri/settings');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);

  React.useEffect(() => {
    setPage(0);
  }, [rows.length]);

  const totalPages = Math.max(1, Math.ceil(rows.length / BREAKDOWN_PAGE_SIZE));
  const start = page * BREAKDOWN_PAGE_SIZE;
  const paginatedRows = useMemo(
    () => rows.slice(start, start + BREAKDOWN_PAGE_SIZE),
    [rows, start],
  );
  const showPagination = rows.length > BREAKDOWN_PAGE_SIZE;

  if (isLoading) {
    return (
      <div className="flex justify-center rounded-xl border border-gray-100 bg-gray-50/30 py-16 dark:border-gray-800 dark:bg-gray-800/30">
        <LoadingSpinner text={t('common:loading')} />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="rounded-xl border border-gray-100 bg-gray-50/30 py-12 text-center text-gray-500 dark:border-gray-800 dark:bg-gray-800/30 dark:text-gray-400">
        {emptyLabel}
      </p>
    );
  }

  const topLabel = primaryHeaderByDepth[0] ?? t('common:group');

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const zebraIndex = { i: 0 };

  return (
    <div>
      <div className="rounded-card border-container-border overflow-hidden border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50 dark:border-gray-800 dark:bg-gray-800/50">
              <th className="px-5 py-3 text-left text-xs font-semibold tracking-tight text-gray-500 dark:text-gray-400">
                {topLabel}
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold tracking-tight text-gray-500 dark:text-gray-400">
                {t('questionsAsked')}
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold tracking-tight text-gray-500 dark:text-gray-400">
                {t('conversations')}
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold tracking-tight text-gray-500 dark:text-gray-400">
                {t('tokens')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {paginatedRows.map((row) => (
              <RowEntry
                key={row.id}
                row={row}
                depth={0}
                expandedKeys={expanded}
                toggle={toggle}
                parentPath=""
                zebraIndex={zebraIndex}
                primaryHeaderByDepth={primaryHeaderByDepth}
              />
            ))}
          </tbody>
        </table>
      </div>
      {showPagination && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            {t('showingRangeOfTotal', {
              from: start + 1,
              to: Math.min(start + BREAKDOWN_PAGE_SIZE, rows.length),
              total: formatNumber(rows.length),
            })}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              {t('previous')}
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              {t('next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
